import { useCallback, useState } from 'react';

import { AudioWaveform } from './audio-waveform';
import { formatTime } from './format-time';
import type { AudioPlayback } from './use-audio-playback';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import type { AudioFileViewerSize } from '../audio-file-viewer';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDownloadAudioTranscriptPdf } from '@/api/mutations/useDownloadAudioTranscriptPdf';
import { useDownloadDocumentV2 } from '@/api/mutations/useDownloadDocumentV2';
import { useGetDocumentAudioWaveform } from '@/api/queries/useGetDocumentAudio';
import type { AudioWaveform as AudioWaveformData } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { cn } from '@/new/lib/utils';

const SKIP_SECONDS = 10;

const PLACEHOLDER_WAVEFORM: AudioWaveformData = {
  peaks: Array.from({ length: 512 }, () => [0, 0]),
};

// The placeholder renders against an empty <audio>, so its errors shouldn't disqualify the real wave.
const ignoreRenderError = () => {};

interface AudioPlayerProps {
  matterId: string;
  docId: string;
  isAudioUrlLoading: boolean;
  playback: AudioPlayback;
  size?: AudioFileViewerSize;
  onClose?: () => void;
}

export function AudioPlayer({
  matterId,
  docId,
  isAudioUrlLoading,
  playback,
  size = 'default',
  onClose,
}: AudioPlayerProps) {
  const { audioProps, currentTime, duration, isPlaying, playbackError, togglePlayback, seekTo, skipBy } = playback;
  const [hasWaveformRenderError, setHasWaveformRenderError] = useState(false);
  // Stable so the waveform effect doesn't tear down on every time update.
  const handleWaveformRenderError = useCallback(() => setHasWaveformRenderError(true), []);

  const { data: waveform, isLoading: isWaveformLoading } = useGetDocumentAudioWaveform(matterId, docId);

  const { mutate: downloadTranscript, isLoading: isDownloadingTranscript } = useDownloadAudioTranscriptPdf({
    matterId,
    docId,
  });

  const handleDownloadTranscript = () => {
    downloadTranscript();
    trackCustomEvent(TrackingEventsV2.DocViewerDownloadAudioTranscript, { matterId, docId });
  };

  const { mutate: downloadOriginal, isLoading: isDownloadingOriginal } = useDownloadDocumentV2({
    matterId,
    docId,
    downloadType: 'original',
  });

  const handleDownloadOriginal = () => {
    downloadOriginal();
    trackCustomEvent(TrackingEventsV2.DocViewerDownloadDocument, { matterId, docId, type: 'original' });
  };

  const closeButton = onClose ? (
    <Button size="iconSm" variant="ghost" onClick={onClose} aria-label="Close document viewer">
      <Icon className="lucide--x" />
    </Button>
  ) : null;

  return (
    <div className={cn('shrink-0 border-b border-gray-200 bg-white', size === 'sm' ? 'p-3 pr-4' : 'p-4.5')}>
      {playbackError ? (
        <div className="flex items-center gap-2">
          <div className={cn('flex grow items-center justify-center gap-2 text-gray-400', size === 'sm' && 'text-xs')}>
            <Icon className="lucide--alert-circle" />
            <p>{playbackError}</p>
          </div>
          {closeButton}
        </div>
      ) : (
        <>
          <audio {...audioProps} crossOrigin="anonymous" preload="metadata" />

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              {size !== 'sm' && (
                <Button
                  size="iconSm"
                  onClick={() => skipBy(-SKIP_SECONDS)}
                  variant="ghost"
                  disabled={isAudioUrlLoading}
                  className="text-gray-500 hover:text-gray-900"
                  aria-label={`Rewind ${SKIP_SECONDS} seconds`}
                >
                  <Icon className="material-symbols--replay-10-rounded text-lg" />
                </Button>
              )}

              <Button
                size="iconSm"
                onClick={togglePlayback}
                variant="ghost"
                disabled={isAudioUrlLoading}
                className={cn('rounded-full bg-black text-white hover:text-white', size === 'sm' ? 'p-0.75' : 'p-1.25')}
                aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
              >
                <Icon
                  className={cn(
                    size === 'sm' ? 'text-base' : 'text-lg',
                    isPlaying ? 'material-symbols--pause-rounded' : 'material-symbols--play-arrow-rounded',
                  )}
                />
              </Button>

              {size !== 'sm' && (
                <Button
                  size="iconSm"
                  onClick={() => skipBy(SKIP_SECONDS)}
                  variant="ghost"
                  disabled={isAudioUrlLoading}
                  className="text-gray-500 hover:text-gray-900"
                  aria-label={`Forward ${SKIP_SECONDS} seconds`}
                >
                  <Icon className="material-symbols--forward-10-rounded text-lg" />
                </Button>
              )}
            </div>

            <div className="flex grow items-center gap-4">
              <span className="w-14 text-right text-xs text-gray-600 tabular-nums">{formatTime(currentTime)}</span>

              {isAudioUrlLoading || isWaveformLoading || !duration ? (
                <AudioWaveform
                  waveform={PLACEHOLDER_WAVEFORM}
                  duration={1}
                  playback={playback}
                  onRenderError={ignoreRenderError}
                  isDisabled
                  size={size}
                />
              ) : waveform && !hasWaveformRenderError ? (
                <AudioWaveform
                  waveform={waveform}
                  duration={duration}
                  playback={playback}
                  onRenderError={handleWaveformRenderError}
                  size={size}
                />
              ) : (
                <input
                  className="h-1.25 min-w-0 flex-1 cursor-pointer appearance-none rounded-full border-0 bg-gray-200 focus-visible:outline-1 focus-visible:outline-offset-3 focus-visible:outline-teal-500 [&::-moz-range-thumb]:h-0 [&::-moz-range-thumb]:w-0 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-transparent [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:h-0 [&::-webkit-slider-thumb]:w-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-transparent"
                  style={{
                    background: `linear-gradient(to right, #1f2937 ${(currentTime / duration) * 100}%, #e5e7eb ${(currentTime / duration) * 100}%)`,
                  }}
                  type="range"
                  min={0}
                  max={duration}
                  step={0.1}
                  value={Math.min(currentTime, duration)}
                  onChange={(event) => seekTo(Number(event.target.value))}
                  onKeyDown={(event) => {
                    if (event.key !== ' ') return;
                    event.preventDefault();
                    void togglePlayback();
                  }}
                  aria-label="Audio position"
                />
              )}

              <span
                className="flex w-14 items-center text-xs text-gray-600 tabular-nums"
                aria-label={isAudioUrlLoading || !duration ? 'Loading audio' : undefined}
              >
                {isAudioUrlLoading || !duration ? (
                  <Icon className="lucide--loader animate-spin text-sm ease-in-out" />
                ) : (
                  formatTime(duration)
                )}
              </span>
            </div>

            {size === 'default' && (
              <PermissionGate permission={Permission.DocumentDownload} uiMode="disabled">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="iconSm"
                      variant="ghost"
                      disabled={isDownloadingTranscript || isDownloadingOriginal}
                      aria-label="Download"
                      className="text-base"
                    >
                      <Icon
                        className={cn(
                          isDownloadingTranscript || isDownloadingOriginal
                            ? 'lucide--loader animate-spin ease-in-out'
                            : 'lucide--download',
                        )}
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top" align="end" sideOffset={10} alignOffset={-4}>
                    <DropdownMenuItem onSelect={handleDownloadTranscript}>
                      <Icon className="lucide--text-align-start" /> Download transcript as PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleDownloadOriginal}>
                      <Icon className="lucide--mic" /> Download original audio
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </PermissionGate>
            )}

            {closeButton}
          </div>
        </>
      )}
    </div>
  );
}
